import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  createFlightRecord,
  createMeetingRecord,
  deleteFlightRecord,
  deleteMeetingRecord,
  getFlightRecords,
  getMeetingRecords,
  getTimePortfolioLookups,
  updateFlightRecord,
  updateMeetingRecord,
} from '../api/timePortfolioApi'
import type {
  FlightRecord,
  FlightRecordPage,
  FlightRecordQuery,
  FlightRecordUpsert,
  MeetingRecord,
  MeetingRecordPage,
  MeetingRecordQuery,
  MeetingRecordUpsert,
} from '../types'

const MEETING_KEY = 'gm-meeting-records'
const FLIGHT_KEY = 'gm-flight-records'
const LOOKUP_KEY = 'gm-meeting-record-lookups'

/**
 * Zaman Portföyü → **Toplantı Kaydı** sunucu-durumu: liste + oluştur/güncelle/sil.
 *
 * Sorgu anahtarı **tüm süzgeçleri ve sayfayı** taşır: sayfalama sunucu tarafındadır, dolayısıyla her
 * süzgeç kombinasyonu ayrı bir sunucu yanıtıdır. Güncel kayıt ayrıca cache'e yazılır (`applyUpdated`)
 * — bayat `modDate` ile ikinci bir kaydetme sahte 409 üretirdi.
 */
export function useMeetingRecords(query: MeetingRecordQuery) {
  const { i18n } = useTranslation()
  const qc = useQueryClient()
  const key = [MEETING_KEY, i18n.language, query]

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [MEETING_KEY] })
  }

  const applyUpdated = (updated: MeetingRecord) =>
    qc.setQueryData<MeetingRecordPage>(key, (prev) =>
      prev
        ? { ...prev, content: prev.content.map((r) => (r.id === updated.id ? updated : r)) }
        : prev,
    )

  const list = useQuery({ queryKey: key, queryFn: () => getMeetingRecords(query) })

  const create = useMutation({
    mutationFn: (body: MeetingRecordUpsert) => createMeetingRecord(body),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: MeetingRecordUpsert }) =>
      updateMeetingRecord(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteMeetingRecord(id),
    onSuccess: invalidate,
  })

  return { list, create, update, remove }
}

/**
 * Formun açılır listeleri. **Dile bağlı** (adlar sunucuda çözülür) ama süzgeçlere bağlı değildir;
 * bu yüzden liste sorgusundan ayrı bir anahtarda tutulur — her sayfa değişiminde yeniden çekilmez.
 */
export function useTimePortfolioLookups() {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: [LOOKUP_KEY, i18n.language],
    queryFn: getTimePortfolioLookups,
    staleTime: 5 * 60 * 1000,
  })
}

/** Zaman Portföyü → **Uçuş Kaydı** sunucu-durumu; kardeş sekmeyle aynı kalıp. */
export function useFlightRecords(query: FlightRecordQuery) {
  const { i18n } = useTranslation()
  const qc = useQueryClient()
  const key = [FLIGHT_KEY, i18n.language, query]

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [FLIGHT_KEY] })
  }

  const applyUpdated = (updated: FlightRecord) =>
    qc.setQueryData<FlightRecordPage>(key, (prev) =>
      prev
        ? { ...prev, content: prev.content.map((r) => (r.id === updated.id ? updated : r)) }
        : prev,
    )

  const list = useQuery({ queryKey: key, queryFn: () => getFlightRecords(query) })

  const create = useMutation({
    mutationFn: (body: FlightRecordUpsert) => createFlightRecord(body),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: FlightRecordUpsert }) =>
      updateFlightRecord(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteFlightRecord(id),
    onSuccess: invalidate,
  })

  return { list, create, update, remove }
}
